var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useRef, useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Search } from '../../assets/icons';
import { Spacing } from '../../constants';
import { getList } from '../../lib/getList';
const CheckboxMenuItem = React.forwardRef((_a, ref) => {
    var { selected, item } = _a, props = __rest(_a, ["selected", "item"]);
    return (React.createElement(MenuItem, Object.assign({ "data-testid": "multiselect-item", selected: selected, value: item.value }, props, { ref: ref }),
        React.createElement(Stack, { direction: "row", gap: Spacing.small },
            React.createElement(Checkbox, { checked: selected }),
            item.label)));
});
export const MultiSelect = (_a) => {
    var _b;
    var { items, groupedItems, label, searchForItems, multiple, onChange, maxLabelItems = 2 } = _a, rest = __rest(_a, ["items", "groupedItems", "label", "searchForItems", "multiple", "onChange", "maxLabelItems"]);
    const inputRef = useRef();
    const selectRef = useRef();
    // cache all the items when the component loads
    const [allItems, setAllItems] = useState(groupedItems !== undefined ? groupedItems.flatMap((gi) => gi.items) : items);
    const [searchText, setSearchText] = useState('');
    useEffect(() => {
        searchForItems(searchText);
    }, [searchText]);
    const isSearching = searchText.length > 0;
    useEffect(() => {
        const updatedItems = groupedItems !== undefined ? groupedItems.flatMap((gi) => gi.items) : items;
        if (isSearching) {
            // if isSearching, just update the isRefined state of the items
            setAllItems((ai) => {
                return ai.map((item) => {
                    const updatedItem = updatedItems.find((ui) => ui.value === item.value);
                    if (!updatedItem) {
                        return item;
                    }
                    return Object.assign(Object.assign({}, item), { isRefined: updatedItem.isRefined });
                });
            });
        }
        else {
            // if not isSearching, update allItems when the props change
            // this is really just to pick up changes caused by other filters
            setAllItems(updatedItems);
        }
    }, [isSearching, items, groupedItems]);
    const singleValue = ((_b = (groupedItems !== undefined ? groupedItems.flatMap((gi) => gi.items) : items).find((i) => i.isRefined)) === null || _b === void 0 ? void 0 : _b.value) || '';
    const multiValue = (groupedItems !== undefined ? groupedItems.flatMap((gi) => gi.items) : items)
        .filter((item) => item.isRefined)
        .map((item) => item.value);
    return (React.createElement(Select, Object.assign({ ref: selectRef, inputRef: inputRef, multiple: multiple, value: multiple ? multiValue : singleValue, onClose: () => {
            // reset the search items
            setTimeout(() => {
                setSearchText('');
            }, 500);
        }, displayEmpty: true, renderValue: () => {
            var _a;
            const displayValue = multiple
                ? getList(allItems.filter((item) => item.isRefined).map((item) => item.label), maxLabelItems)
                : ((_a = allItems.find((item) => item.isRefined)) === null || _a === void 0 ? void 0 : _a.label) || '';
            return (React.createElement(Stack, { direction: "row", gap: Spacing.small },
                React.createElement(Typography, { "data-testid": "multiselect-label" }, label),
                !!displayValue.length && (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: "vertical-divider" }),
                    React.createElement(Typography, { variant: "bodyLargeHeavy", "data-testid": "multiselect-value" }, displayValue)))));
        } }, rest),
        React.createElement(ListSubheader, null,
            React.createElement(TextField, { "data-testid": "multiselect-search", autoFocus: true, placeholder: "Search...", variant: "standard", value: searchText, onChange: (e) => {
                    setSearchText(e.target.value);
                }, onKeyDown: (e) => {
                    if (e.key !== 'Escape') {
                        // Prevents autoselecting item while typing (default Select behaviour)
                        e.stopPropagation();
                    }
                }, InputProps: {
                    startAdornment: (React.createElement(InputAdornment, { position: "start" },
                        React.createElement(Search, null))),
                } })),
        items !== undefined
            ? items.map((item) => (React.createElement(CheckboxMenuItem, { key: item.value, item: item, value: item.value, selected: multiple ? multiValue.includes(item.value) : singleValue === item.value, onClick: () => {
                    onChange(item.value);
                    setSearchText('');
                } })))
            : groupedItems.flatMap((group) => group.items.flatMap((item, index) => {
                return [
                    ...(index === 0
                        ? [
                            React.createElement(ListSubheader, { "data-testid": "multiselect-subheader", key: group.label }, group.label),
                        ]
                        : []),
                    React.createElement(CheckboxMenuItem, { key: item.value, item: item, value: item.value, selected: multiple ? multiValue.includes(item.value) : singleValue === item.value, onClick: () => {
                            onChange(item.value);
                            setSearchText('');
                        } }),
                ];
            }))));
};
